import React from "react";
import { Link } from "react-router-dom";
import Icon from "../AppIcon";

/**
 * Strategy descriptions explaining how each distribution type calculates employee shares
 */
export const STRATEGY_DESCRIPTIONS = {
  0: {
    name: "Equal",
    title: "Equal Split",
    formula: "Total TRONC / Number of Eligible Employees",
    description: "Distributes TRONC equally among all eligible employees regardless of role, hours worked, or seniority.",
    example: "If £1,000 is to be distributed among 5 employees, each receives £200.",
    pros: ["Simple and transparent", "Easy to understand and audit", "No complex calculations"],
    cons: ["Doesn't reward experience or effort", "May not reflect contribution differences"],
  },
  1: {
    name: "ByJobRole",
    title: "By Job Role",
    formula: "Min Floor + Remainder × (RoleScore × HourScore × SeniorityScore) / Σ(all employee scores)",
    description: "Assign a score to each job role. Each employee's share of the remainder is proportional to their role score multiplied by their hour score (1–2) and seniority score (1–2).",
    example: "Waiters (score 2), Chefs (score 1): the waiter with most hours and highest seniority earns the largest individual share.",
    pros: ["Rewards role seniority and hours worked", "Multiplicative — all factors combine naturally", "Configurable per job role"],
    cons: ["Requires job role setup", "Hour & seniority scores require clock/HR data"],
    requiresConfig: true,
    linkTo: "/settings/job-roles",
    linkText: "Manage Job Roles",
  },
  2: {
    name: "ByDepartment",
    title: "By Department",
    formula: "Min Floor + Remainder × (DeptScore × HourScore × SeniorityScore) / Σ(all employee scores)",
    description: "Assign a score to each department. Each employee's share of the remainder is proportional to their department score multiplied by their hour score (1–2) and seniority score (1–2).",
    example: "FOH (score 2), BOH (score 1): a senior FOH employee with long shifts earns the largest individual share.",
    pros: ["Groups by operational area", "Multiplicative — all factors combine naturally", "Simpler than per-role config"],
    cons: ["Requires department assignment on job roles"],
    requiresConfig: true,
  },
};

/**
 * Distribution Rule Type Selector
 * Shows side-by-side boxes for selecting/displaying distribution types
 * Simplified to 3 core types: Equal, Role Score, By Department
 */
const RuleTypeSelector = ({
  selectedType,
  onSelect = null,
  readonly = false,
  className = "",
  showDescriptions = true,
}) => {
  const distributionTypes = [
    {
      type: 0,
      name: "Equal",
    title: "Equal Split",
      description: "Divide equally among all eligible employees",
      icon: "Users",
      color: "var(--color-primary)",
    },
  {      type: 1,
      name: "ByJobRole",
      title: "By Job Role",
      description: "Assign score multipliers to each job role — proportional share",
      icon: "Briefcase",
  color: "var(--color-secondary)",
    },
    {
      type: 2,
      name: "ByDepartment",
      title: "By Department",
      description: "Assign score multipliers to departments (FOH, BOH, etc.)",
      icon: "Building2",
   color: "var(--color-accent)",
    },
  ];

  // Normalize selected type to number
  const normalizeType = (type) => {
    if (typeof type === "string") {
      const typeMap = {
        Equal: 0,
        ByJobRole: 1,
        RoleScore: 1,
        ByDepartment: 2,
        WeightedHours: 0, // legacy — map to Equal
      };
    return typeMap[type] ?? 0;
    }
    if (type > 2) return 0;   // Any unknown/legacy type -> Equal
    return type;
  };

  const currentType = normalizeType(selectedType);

  // Get strategy description for selected type
  const selectedStrategy = STRATEGY_DESCRIPTIONS[currentType];

  return (
 <div className={className}>
      {/* Distribution Types Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {distributionTypes.map((typeInfo) => {
      const isSelected = currentType === typeInfo.type;
  const isClickable = !readonly && onSelect;

      return (
          <button
       key={typeInfo.type}
  type="button"
    onClick={() => isClickable && onSelect(typeInfo.type)}
      disabled={!isClickable}
  className={`p-5 border-2 rounded-xl text-center transition-all ${
       isSelected
                ? "border-primary bg-primary/10 shadow-md"
     : readonly
           ? "border-border bg-muted cursor-default"
  : "border-border hover:border-primary/30 hover:bg-primary/5 cursor-pointer"
              } ${!isClickable ? "cursor-default" : ""}`}
    >
       {/* Icon */}
    <div
     className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center transition-colors"
  style={{
    backgroundColor: isSelected
      ? typeInfo.color
        : "var(--color-muted)",
        }}
  >
                <Icon
           name={typeInfo.icon}
         size={24}
       className={isSelected ? "text-white" : "text-muted-foreground"}
                />
    </div>

   {/* Title */}
              <h4 className="font-semibold mb-1">{typeInfo.title}</h4>

        {/* Description */}
    <p className="text-sm text-muted-foreground leading-tight">
         {typeInfo.description}
              </p>

    {/* Selected Indicator */}
{isSelected && (
  <div className="mt-3">
          <Icon
        name="CheckCircle"
              size={20}
     className="text-primary mx-auto"
         />
      </div>
          )}
  </button>
          );
        })}
      </div>

      {/* Strategy Description Panel - Shows when a type is selected */}
      {showDescriptions && selectedStrategy && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
  <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon name="Calculator" size={20} className="text-primary" />
          </div>
        <div className="flex-1 min-w-0">
       <h4 className="font-semibold text-sm mb-1">
             {selectedStrategy.title} Strategy
        </h4>
         <p className="text-sm text-muted-foreground mb-3">
       {selectedStrategy.description}
     </p>

              {/* Formula */}
         <div className="mb-3">
           <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
        Calculation Formula
         </p>
           <code className="text-xs bg-background px-2 py-1 rounded border border-border inline-block">
      {selectedStrategy.formula}
              </code>
              </div>

  {/* Example */}
              <div className="mb-3 p-2 bg-background rounded border border-border">
           <p className="text-xs font-semibold text-muted-foreground mb-1">Example</p>
                <p className="text-xs">{selectedStrategy.example}</p>
              </div>

   {/* Pros and Cons */}
      <div className="grid grid-cols-2 gap-3">
        <div>
     <p className="text-xs font-semibold text-success mb-1 flex items-center gap-1">
                <Icon name="Check" size={12} />
    Advantages
                  </p>
           <ul className="text-xs text-muted-foreground space-y-0.5">
   {selectedStrategy.pros.map((pro, i) => (
          <li key={i} className="flex items-start gap-1">
           <span className="text-success shrink-0">•</span>
 {pro}
          </li>
       ))}
         </ul>
           </div>
 <div>
              <p className="text-xs font-semibold text-warning mb-1 flex items-center gap-1">
        <Icon name="AlertTriangle" size={12} />
            Considerations
          </p>
         <ul className="text-xs text-muted-foreground space-y-0.5">
         {selectedStrategy.cons.map((con, i) => (
            <li key={i} className="flex items-start gap-1">
   <span className="text-warning shrink-0">•</span>
      {con}
            </li>
            ))}
                  </ul>
        </div>
    </div>

            {/* Link to Job Roles for Role Score */}
              {selectedStrategy.linkTo && (
     <div className="mt-3 p-2 bg-info/10 border border-info/20 rounded flex items-center gap-2">
            <Icon name="ExternalLink" size={14} className="text-info shrink-0" />
 <Link 
     to={selectedStrategy.linkTo} 
    className="text-xs text-info hover:underline font-medium"
       >
         {selectedStrategy.linkText}
  </Link>
        </div>
              )}

              {/* Requires Configuration Badge */}
          {selectedStrategy.requiresConfig && (
                <div className="mt-3 flex items-center gap-2">
         <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
   <Icon name="Settings" size={12} />
     Requires configuration below
    </span>
         </div>
              )}
 </div>
 </div>
        </div>
      )}
    </div>
  );
};

export default RuleTypeSelector;
